import moment from 'moment-timezone'
import React, { useState, useEffect, useContext } from 'react'
import { Button } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { listBarrierTypeIcons } from 'plugin-codaf-risk-ext-bowtie/services/barrierTypeIcon'
import {
    CustomList,
    FilteredSearch,
    RemoveConfirmationModal
} from 'plugin-generic-components/shared/components/components'

import { listBarrierTypes, deleteBarrierType } from '../../../services'
import { IBarrierType, IBarrierTypeIcon, IPaginatedREST } from '../../../types'
import { PAGINATION_SIZE } from '../../../utils/constants'
import {
    BowtieBarrierSettingsRouteContext,
    IBowtieBarrierSettingsRouteContext,
    IQueryParamsList
} from '../../BowtieBarrierSettingWrapper'
import { AddBarrierCategoriesModal } from './AddBarrierCategoriesModal/AddBarrierCategoriesModal'
import { BarrierTypeListContainer } from './styles'

interface IProps {
    systemProperty: string
}

export const BarrierCategories: React.FC<IProps> = ({ systemProperty }) => {
    const [barrierRequest, setBarrierRequest] = useState<IPaginatedREST<IBarrierType>>({
        total: 0,
        totalPages: 0,
        data: [],
        index: 0,
        size: 15
    })
    const [showAddBarrierCategoriesModal, setShowAddBarrierCategoriesModal] = useState(false)
    const [showRemoveBarrierTypeModal, setShowRemoveBarrierTypeModal] = useState(false)
    const [tempCategory, setTempCategory] = useState<IBarrierType>()
    const [loading, setLoading] = useState<boolean>(false)
    const { params, setParams } = useContext<IBowtieBarrierSettingsRouteContext<IQueryParamsList>>(
        BowtieBarrierSettingsRouteContext
    )
    const [barrierTypeIcons, setBarrierTypeIcons] = useState<IBarrierTypeIcon[]>()
    const [searchTerm, setSearchTerm] = useState<string>('')

    // const barrierTypeIconMap: Map<number, string> = new Map()

    const handleGetBarrierTypeIcons = async (): Promise<void> => {
        try {
            const data = await listBarrierTypeIcons()

            setBarrierTypeIcons(data.data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on getting barrier type icons.'))
        }
    }

    const handleGetBarrierCategories = async (): Promise<void> => {
        try {
            setLoading(true)
            const barrierTypeFetch = await listBarrierTypes(params)
            setBarrierRequest(barrierTypeFetch)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on getting operations.'))
        } finally {
            setLoading(false)
        }
    }

    const handleGetFaNameFromBarrierTypeIcon = (barrierCategory: IBarrierType): string => {
        if (barrierTypeIcons !== null && barrierTypeIcons !== undefined && barrierTypeIcons.length > 0) {
            const relatedIcon = barrierTypeIcons?.find(icon => icon?.id === barrierCategory?.barrierTypeIcon?.id)

            return relatedIcon?.faName
        }

        return ''
    }

    const handleSearch = async (keyword?: string): Promise<void> => {
        setParams({ ...params, keyword })
    }

    const handleSortList = async (orderBy: string, direction: 'ASC' | 'DESC'): Promise<void> => {
        setParams({ ...params, orderBy, direction })
    }

    const handlePerPageChange = async (perPage: number): Promise<void> => {
        setParams({ ...params, perPage })
    }

    const handleNewBarrierCategory = (): void => {
        setShowAddBarrierCategoriesModal(true)
    }

    const handleDeleteBarrierType = (id: number): void => {
        setTempCategory(barrierRequest.data.find(b => b.id === id))
        setShowRemoveBarrierTypeModal(true)
    }

    const handleCloseDeleteBarrierType = (): void => {
        handleGetBarrierCategories()
        setShowRemoveBarrierTypeModal(false)
        setTempCategory(null)
    }

    const handleCloseAddBarrierCategoriesModal = (): void => {
        setShowAddBarrierCategoriesModal(false)
        setTempCategory(null)
    }

    useEffect(() => {
        handleGetBarrierCategories()
    }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        handleGetBarrierTypeIcons()
    }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <BarrierTypeListContainer>
            <div style={{ display: 'flex', flexDirection: 'row', marginTop: 32, marginBottom: 32 }}>
                <Button
                    className="no-outline-button"
                    bsStyle="primary"
                    onClick={handleNewBarrierCategory}
                    style={{ borderRadius: 3, marginRight: 8 }}
                >
                    <i className="far fa-plus custom-plus" /> {i18n('New Category')}
                </Button>
                <FilteredSearch
                    keyword={params.keyword}
                    selectedFilterOption={params.filterBy}
                    onSearch={e => setSearchTerm(e)}
                />
            </div>
            {loading ? (
                <div className={'loadingSpin'}>
                    <i className="fa fa-spinner fa-spin fa-x2" />
                </div>
            ) : barrierRequest.data && barrierRequest.data.length ? (
                <CustomList
                    label={i18n('barrier categories')}
                    list={barrierRequest.data
                        ?.filter(
                            bc =>
                                (bc.author ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                bc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                bc.name.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map(barrierCategory => ({
                            id: barrierCategory.id,
                            iconReference: handleGetFaNameFromBarrierTypeIcon(barrierCategory) ?? '',
                            name: barrierCategory.name,
                            description: i18n(barrierCategory.description),
                            author: barrierCategory.author,
                            updatedAt:
                                moment.utc(barrierCategory.updatedAt).format('DD/MM/YYYY, hh:mm:ss a') ===
                                'Invalid date'
                                    ? 'Created by default'
                                    : moment.utc(barrierCategory.updatedAt).format('DD/MM/YYYY, hh:mm:ss a'),
                            deleteable: barrierCategory.deleteable
                        }))}
                    columns={[
                        { title: 'ICON', listRef: 'iconReference', sortable: true, type: 'icon' },
                        { title: 'CATEGORY', listRef: 'name', sortable: true },
                        { title: 'DESCRIPTION', listRef: 'description', sortable: true },
                        { title: 'AUTHOR', listRef: 'author', sortable: true },
                        { title: 'LAST CHANGED', listRef: 'updatedAt', sortable: true }
                    ]}
                    sortField={params.orderBy === 'name' ? 'name' : params.orderBy}
                    onSortClick={handleSortList}
                    sortDirection={params.direction}
                    tableProps={{ style: { width: '100%' } }}
                    configOptions={[
                        {
                            icon: 'far fa-edit',
                            onClickConfig: k =>
                                setParams({ type: 'edit', tab: 'barrierCategories', id: k.id, system: systemProperty })
                        },
                        {
                            icon: 'far fa-trash-alt',
                            onClickConfig: k => handleDeleteBarrierType(k.id),
                            hidden: k => !k.deleteable
                        }
                    ]}
                    pagination={{
                        type: 'live',
                        totalItems: barrierRequest.data?.filter(
                            bc =>
                                (bc?.author ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                                bc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                bc.name.toLowerCase().includes(searchTerm.toLowerCase())
                        ).length,
                        showingItems: barrierRequest.size,
                        onPerPageChange: () => handlePerPageChange((params.perPage += PAGINATION_SIZE))
                    }}
                    fixedHeader={true}
                />
            ) : (
                <div className="no-bowtie">
                    <i className="far fa-folder-open" />
                    <p>{i18n('There are no Categories yet')}</p>
                    <a onClick={handleNewBarrierCategory}>{i18n('Create new Category')}</a>
                </div>
            )}

            <AddBarrierCategoriesModal
                handleClose={handleCloseAddBarrierCategoriesModal}
                handleSave={id => setParams({ type: 'edit', tab: 'barrierCategories', id: id, system: systemProperty })}
                show={showAddBarrierCategoriesModal}
                item={tempCategory}
            />

            <RemoveConfirmationModal
                setShow={_ => {
                    setShowRemoveBarrierTypeModal(false)
                }}
                handleClose={handleCloseDeleteBarrierType}
                show={showRemoveBarrierTypeModal}
                onDelete={async () => {
                    await deleteBarrierType(tempCategory.id)
                    handleCloseDeleteBarrierType()
                }}
                type={'category'}
                removalMessage={`${i18n('Are you sure you want remove the category')} ${tempCategory?.name}?`}
            />
        </BarrierTypeListContainer>
    )
}
